import React from 'react';

import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { TextComponent } from 'design/text/textComponents';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { MarginComponent } from 'design/margin';
import {
  SmallTableRowComponent,
  SmallTableWrapperComponent,
} from 'design/smallTable';
import { useServices } from 'hooks/useServices';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';
import { PartnerManagerData } from 'types/partnerManager';
import { AvatarComponent } from 'design/avatar';

type Props = {
  partnerId: number;
  parnerManagerData: PartnerManagerData;
  onEditProfile(): void;
  onEditCredentials(): void;
  onEditPassword(): void;
  onBlockChanged(): void;
};

export const PartnerManagerSummaryScreen = (props: Props) => {
  const {
    partnerId,
    parnerManagerData,
    onEditPassword: editPassowrd,
    onEditProfile: editProfile,
    onEditCredentials: editCredentials,
    onBlockChanged,
  } = props;

  const dispatch = useAppDispatcher();

  const {
    updatePartnerCode: updatePartnerCodeService,
    changePartnerBlock,
  } = useServices();

  const { injectPromise, active: asyncActive } = useAsyncBoundary();

  const {
    shared: sharedDitctionary,
    partnerManager: {
      summaryScreen: ownDictionary,
      partnerInfoTable: partnerInfoTableDictionary,
    },
  } = useIntlDictionary();

  const {
    partner,
    partnersTableItem,
    partnerCode,
    partnerBlocked,
  } = parnerManagerData;

  const { profile: partnerProfile } = partner;

  const partnerFullName = sharedDitctionary.partnerFullName(partnerProfile);

  const updatePartnerCode = React.useCallback(() => {
    const promise = updatePartnerCodeService({ partnerId }).then(
      (nextPartnerCode) => {
        dispatch({
          name: 'PartnerManager__PartnerCodeUpdated',
          nextPartnerCode,
        });
      }
    );

    injectPromise(promise, { shouldShowFallback: false });
  }, [dispatch, injectPromise, partnerId, updatePartnerCodeService]);

  const blockPartner = React.useCallback(() => {
    injectPromise(changePartnerBlock(true, partnerId).then(onBlockChanged), {
      shouldShowFallback: false,
    });
  }, [changePartnerBlock, injectPromise, onBlockChanged, partnerId]);

  const unblockPartner = React.useCallback(() => {
    injectPromise(changePartnerBlock(false, partnerId).then(onBlockChanged), {
      shouldShowFallback: false,
    });
  }, [changePartnerBlock, injectPromise, onBlockChanged, partnerId]);

  return (
    <>
      <SidebarModalSectionComponent
        content={
          <>
            {partnerProfile.avatarURL && (
              <>
                <AvatarComponent url={partnerProfile.avatarURL} alt="avatar" />
                <MarginComponent top={32} />
              </>
            )}
            <TextComponent size="mp" weight="medium">
              {partnerInfoTableDictionary.titleText}
            </TextComponent>
            <MarginComponent top={20} />
            <SmallTableWrapperComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.fullNameLabel}
                </TextComponent>
                <TextComponent>{partnerFullName}</TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.partnerCodeLabel}
                </TextComponent>
                <TextComponent>
                  {partnerCode}
                  {'   '}
                  <ClickControlComponent onClick={updatePartnerCode}>
                    <TextComponent color="accent">
                      {partnerInfoTableDictionary.generateSecretCodeText}
                    </TextComponent>
                  </ClickControlComponent>
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.emailLabel}
                </TextComponent>
                <TextComponent>{partnersTableItem.email}</TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.phoneLabel}
                </TextComponent>
                <TextComponent>{partnersTableItem.phone}</TextComponent>
              </SmallTableRowComponent>
              {partnerProfile.country && partnerProfile.city && (
                <SmallTableRowComponent>
                  <TextComponent>
                    {partnerInfoTableDictionary.locationLabel}
                  </TextComponent>
                  <TextComponent>
                    {partnerInfoTableDictionary.locationText(
                      partnerProfile.country,
                      partnerProfile.city
                    )}
                  </TextComponent>
                </SmallTableRowComponent>
              )}
              <SmallTableRowComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.clientsCountLabel}
                </TextComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.clientsCountText(
                    partnersTableItem.clientsCount
                  )}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.balanceLabel}
                </TextComponent>
                <TextComponent>
                  {sharedDitctionary.moneyAmountText(
                    partnersTableItem.balance,
                    'USD'
                  )}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.registrationDateLabel}
                </TextComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.registrationDateText(
                    partner.createdOnDate
                  )}
                </TextComponent>
              </SmallTableRowComponent>
              {partnersTableItem.latestUpdateDate && (
                <SmallTableRowComponent>
                  <TextComponent>
                    {partnerInfoTableDictionary.latestUpdateDateLabel}
                  </TextComponent>
                  <TextComponent>
                    {partnerInfoTableDictionary.latestUpdateDateText(
                      partnersTableItem.latestUpdateDate
                    )}
                  </TextComponent>
                </SmallTableRowComponent>
              )}
              <SmallTableRowComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.idLabel}
                </TextComponent>
                <TextComponent>{partnersTableItem.id}</TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.statusLabel}
                </TextComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.statusText(
                    partnersTableItem.status
                  )}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {partnerInfoTableDictionary.detailsLabel}
                </TextComponent>
                <TextComponent>{partnerProfile.details}</TextComponent>
              </SmallTableRowComponent>
            </SmallTableWrapperComponent>
          </>
        }
        actions={
          <>
            <ModalPressControlComponent
              type="button"
              theme="secondary"
              disabled={asyncActive}
              onPress={editProfile}
            >
              {ownDictionary.editProfileActionText}
            </ModalPressControlComponent>
            <MarginComponent top={20} />
            <ModalPressControlComponent
              type="button"
              theme="secondary"
              disabled={asyncActive}
              onPress={editCredentials}
            >
              {ownDictionary.editCredentialsActionText}
            </ModalPressControlComponent>
            <MarginComponent top={20} />
            <ModalPressControlComponent
              type="button"
              theme="secondary"
              disabled={asyncActive}
              onPress={editPassowrd}
            >
              {ownDictionary.editPasswordActionText}
            </ModalPressControlComponent>
            <MarginComponent top={20} />
            {partnerBlocked ? (
              <ModalPressControlComponent
                type="button"
                theme="secondary"
                disabled={asyncActive}
                onPress={unblockPartner}
              >
                {ownDictionary.unblockPartnerActionText}
              </ModalPressControlComponent>
            ) : (
              <ModalPressControlComponent
                type="button"
                theme="secondary"
                disabled={asyncActive}
                onPress={blockPartner}
              >
                {ownDictionary.blockPartnerActionText}
              </ModalPressControlComponent>
            )}
          </>
        }
      />
    </>
  );
};
